<template>
  <!-- SEC-374: how a Safe-4 operator classified the alarm on the tracking link.
       Renders nothing when the link was never opened and nothing was chosen. -->
  <div v-if="hasSafe4Classification(classification)" class="text-xs" :class="{ 'mt-1': !full }">
    <div v-if="classification.value">
      <span class="safe4-dot" :style="{ backgroundColor: dotColor }"></span>
      <span class="font-semibold">{{ safe4ClassificationLabel(classification.value) }}</span>
      <span v-if="!full && previousValue" class="text-secondary-500">
        &middot; {{ $t("safe4_classification.changed_was", { value: safe4ClassificationLabel(previousValue) }) }}
      </span>
    </div>

    <!-- Table: one short line with both times -->
    <div v-if="!full" class="text-xxs text-secondary-500">
      <span v-if="classification.opened_at">{{ $t("safe4_classification.link_opened") }} {{ formatTime(classification.opened_at) }}</span>
      <span v-if="classification.opened_at && showClassifiedTime"> &middot; </span>
      <span v-if="showClassifiedTime">{{ $t("safe4_classification.classified") }} {{ formatTime(classification.at) }}</span>
    </div>

    <!-- Detail window: one line per time, and every change -->
    <template v-if="full">
      <div v-if="classification.opened_at" class="text-secondary-500 mt-1">
        {{ $t("safe4_classification.link_opened") }}: {{ formatTime(classification.opened_at) }}
      </div>
      <div v-if="showClassifiedTime" class="text-secondary-500 mt-1">
        {{ $t("safe4_classification.classified") }}: {{ formatTime(classification.at) }}
      </div>
      <div v-if="previousValue" class="text-secondary-500 mt-1">
        {{ $t("safe4_classification.changed") }}: {{ changeHistoryText }}
      </div>
    </template>
  </div>
</template>

<script>
// Same colours as the buttons on the Safe-4 page (safe4_track_alarm.vue)
const DOT_COLORS = {
  test: "#15803d",
  false: "#ca8a04",
  confirmed: "#b42318",
};

export default {
  name: "Safe4Classification",
  props: {
    // alarm_classification from a gps_log or ac_report_log row
    classification: { type: Object, default: null },
    // When the alarm happened. In the table, a time on the same day is shown
    // without its date.
    eventAt: { type: [String, Date], default: null },
    // true in the detail window: full dates and the whole change history
    full: { type: Boolean, default: false },
    // Optional: the page's own date format, so the times match the rest of
    // the page. Without it, Swedish time is used, like the rest of SecurTool.
    dateFormatter: { type: Function, default: null },
  },
  computed: {
    dotColor() {
      return DOT_COLORS[this.classification.value] || "#6b7280";
    },

    showClassifiedTime() {
      return Boolean(this.classification.value && this.classification.at);
    },

    history() {
      return this.classification.history || [];
    },

    // The choice before the current one, when it was changed
    previousValue() {
      if (this.history.length == 0) return "";
      return this.history[this.history.length - 1].value;
    },

    // e.g. "Testlarm 2026-10-12 14:03:10 → Falsklarm 2026-10-12 14:03:55"
    changeHistoryText() {
      const steps = [];
      for (const h of this.history) {
        steps.push(`${this.safe4ClassificationLabel(h.value)} ${this.formatTime(h.at)}`);
      }
      steps.push(`${this.safe4ClassificationLabel(this.classification.value)} ${this.formatTime(this.classification.at)}`);
      return steps.join(" → ");
    },
  },
  methods: {
    // The page's own format when it gives one. Otherwise Swedish time, like the
    // rest of SecurTool, and in the table without the date when it is the same
    // day as the alarm.
    formatTime(date) {
      if (!date) return "";
      if (this.dateFormatter) return this.dateFormatter(date);

      const time = this.moment(date).tz("Europe/Stockholm");

      if (!this.full && this.eventAt) {
        const eventDay = this.moment(this.eventAt).tz("Europe/Stockholm").format("YYYY-MM-DD");
        if (time.format("YYYY-MM-DD") == eventDay) return time.format("HH:mm:ss");
      }

      return time.format("YYYY-MM-DD HH:mm:ss");
    },
  },
};
</script>

<style scoped>
.safe4-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 4px;
  vertical-align: middle;
}
</style>
